/* PP SHOWCASE — the arrows for the carousel layout of sections/pp-showcase.liquid (deck slide 16).
 *
 * Progressive enhancement, the same deal as pp-mascot.js but inverted. The row itself is a plain
 * overflow-x container: swipe, trackpad and the native scrollbar all work with this file blocked,
 * missing or throwing. So the section ships the arrows `hidden` and THIS script reveals them —
 * a mascot with no script still says something, but an arrow that cannot scroll anything is a
 * control that lies to the shopper.
 *
 * Smooth versus instant scrolling is deliberately NOT decided here: scrollTo is called without a
 * `behavior`, which means "use the element's CSS scroll-behavior", and pp-home.css only turns that
 * on inside @media (prefers-reduced-motion: no-preference). One source of truth, and the visitor's
 * setting wins without this file ever asking about it.
 *
 * No dependencies, no framework.
 */
(() => {
  // scrollLeft rarely lands exactly on 0 or exactly on the maximum — fractional browser zoom and
  // non-integer device pixel ratios leave sub-pixel remainders. Without this slack the arrow at an
  // end would flicker back on for the sake of half a pixel.
  const EDGE_SLOP = 2;

  const maxScroll = (track) => track.scrollWidth - track.clientWidth;

  // One arrow press moves as many WHOLE cards as currently fit. Rounding down to whole cards leaves
  // the row on a card edge, which is also where scroll-snap wants it, so the two never argue.
  const pageSize = (track) => {
    const cell = track.firstElementChild;
    const cellWidth = cell ? cell.getBoundingClientRect().width : 0;
    if (cellWidth <= 0) return track.clientWidth;
    return Math.max(1, Math.floor(track.clientWidth / cellWidth)) * cellWidth;
  };

  // Returns false when the row is not wired up — the caller uses that to decide whether the element
  // may be marked as done. The custom element below can be connected before its own children have
  // been parsed, and marking THAT as ready would leave a carousel with permanently dead arrows.
  const setup = (root) => {
    const track = root.querySelector('[data-pp-carousel-track]');
    const prev = root.querySelector('[data-pp-carousel-prev]');
    const next = root.querySelector('[data-pp-carousel-next]');
    if (!track || !prev || !next) return false;

    // Where the last arrow press asked the row to land. Without it, a shopper clicking an arrow
    // twice quickly gets less than two pages: the second scroll would start from wherever the
    // still-running smooth scroll happened to be at that instant, not from where it was heading.
    let target = null;
    let frame = 0;

    const disable = (btn, sibling) => {
      if (btn.disabled) return;
      const hadFocus = document.activeElement === btn;
      btn.disabled = true;
      // A disabled button drops out of the tab order, taking the keyboard shopper's place in the
      // page down to <body> with it. Hand focus to the arrow that still works instead.
      if (hadFocus && !sibling.disabled) sibling.focus();
    };

    const sync = () => {
      const limit = maxScroll(track);
      // limit <= slop means everything already fits (few products, or a wide viewport): both arrows
      // go quiet rather than sitting there doing nothing when pressed.
      const atStart = limit <= EDGE_SLOP || track.scrollLeft <= EDGE_SLOP;
      const atEnd = limit <= EDGE_SLOP || track.scrollLeft >= limit - EDGE_SLOP;

      if (target !== null && Math.abs(track.scrollLeft - target) <= EDGE_SLOP) target = null;

      // Enable first, disable second: when an arrow holding focus is about to be disabled, the
      // sibling it hands focus to has to be enabled already.
      if (!atStart) prev.disabled = false;
      if (!atEnd) next.disabled = false;
      if (atStart) disable(prev, next);
      if (atEnd) disable(next, prev);
    };

    // Scroll fires far more often than the arrows can actually change state, so collapse a burst
    // into one update per frame. A frame is also exactly when the browser would repaint the result.
    const requestSync = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        sync();
      });
    };

    const step = (direction) => {
      const from = target === null ? track.scrollLeft : target;
      const to = Math.max(0, Math.min(maxScroll(track), from + direction * pageSize(track)));
      target = to;
      track.scrollTo({ left: to });
    };

    prev.addEventListener('click', () => step(-1));
    next.addEventListener('click', () => step(1));

    // Any scroll the shopper starts themselves makes the arrows' pending destination meaningless.
    for (const type of ['pointerdown', 'wheel', 'touchstart', 'keydown']) {
      track.addEventListener(type, () => { target = null; }, { passive: true });
    }

    track.addEventListener('scroll', requestSync, { passive: true });
    window.addEventListener('resize', requestSync, { passive: true });
    // Catches width changes the window never sees: the Theme Editor pane opening, a container query
    // above us, a zoom change. Cards are sized in percentages, so the box is all we need to watch.
    if (typeof ResizeObserver === 'function') new ResizeObserver(requestSync).observe(track);

    prev.hidden = false;
    next.hidden = false;
    sync();
    return true;
  };

  // The "done" flag is written only after a SUCCESSFUL setup, so a row whose children were not in
  // the DOM yet stays eligible for the later sweeps instead of being written off.
  const initOne = (el) => {
    if (el.dataset.ppCarouselReady) return;
    if (setup(el)) el.dataset.ppCarouselReady = '1';
  };

  const initAll = (root) => {
    for (const el of (root || document).querySelectorAll('[data-pp-carousel]')) initOne(el);
  };

  // The script is deferred, so the document is usually parsed by now — but a section re-rendered in
  // the Theme Editor can load it mid-parse, hence the readyState guard.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // The Theme Editor replaces a section's entire markup when a setting changes, throwing away our
  // listeners and re-hiding the arrows. Re-scan the section that was rebuilt.
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  // The product page's recommendation strip is not in the page at load time: it is fetched through
  // the Section Rendering API and morphed in seconds later — after DOMContentLoaded, and without a
  // shopify:section:load event, so neither sweep above ever sees it. A custom element is the one
  // hook the platform itself fires on morphed-in markup (it is how Horizon's own product-card and
  // quick-add come back to life), which is why that strip wraps its row in <pp-carousel> while the
  // homepage keeps its plain <div data-pp-carousel>. Both carry data-pp-carousel and both end up in
  // the same initOne, so there is still one implementation of the arrows.
  if (typeof customElements === 'object' && !customElements.get('pp-carousel')) {
    customElements.define(
      'pp-carousel',
      class extends HTMLElement {
        connectedCallback() {
          initOne(this);
        }
      }
    );
  }
})();
